// Phone numbers, as Rhema uses them to find people. The server only ever sees E.164 numbers, such
// as +447700900123, so every number is normalised here first.

import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";

export type { CountryCode };

export const FALLBACK_REGION: CountryCode = "US";

const REGIONS = new Set<string>(getCountries());

/** Whether a string is a region libphonenumber knows, such as "NG" or "GB". */
export function isRegion(code: string | null | undefined): code is CountryCode {
  return Boolean(code && REGIONS.has(code));
}

/** The region of a locale such as "en-GB" or "yo-NG", or the fallback when it has none. */
export function regionFromLocale(
  locale: string | null | undefined,
  fallback: CountryCode = FALLBACK_REGION,
): CountryCode {
  if (!locale) return fallback;
  let region: string | undefined;
  try {
    region = new Intl.Locale(locale).maximize().region;
  } catch {
    region = locale.split(/[-_]/)[1]?.toUpperCase();
  }
  return isRegion(region) ? region : fallback;
}

/**
 * A number as typed or stored in an address book, in E.164, or null when it cannot be a number.
 * Numbers without a leading + or 00 are read as numbers in `region`.
 */
export function normalisePhone(raw: string, region: CountryCode = FALLBACK_REGION): string | null {
  const text = raw.trim().replace(/^00/, "+");
  if (!/\d/.test(text)) return null;
  const parsed = parsePhoneNumberFromString(text, region);
  // Only the length is checked here, so new number ranges and the +1 555 test numbers pass. The
  // server and SBI make the final check.
  if (!parsed?.isPossible()) return null;
  return parsed.number;
}

/** An E.164 number in international format for display, such as +44 7700 900123. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const parsed = parsePhoneNumberFromString(e164);
  return parsed ? parsed.formatInternational() : e164;
}

/** The digits of a number without the +, as used in /chat/ links. */
export function digitsOf(e164: string): string {
  return e164.replace(/\D/g, "");
}

/** The number for /chat/<digits> links, or null when the digits are not a valid number. */
export function phoneFromDigits(digits: string): string | null {
  if (!/^\d{7,15}$/.test(digits)) return null;
  return normalisePhone(`+${digits}`);
}

/** The link that opens a chat with a number: https://app.rhema.siliconblast.com/chat/<digits>. */
export function chatLink(origin: string, e164: string): string {
  return `${origin.replace(/\/+$/, "")}/chat/${digitsOf(e164)}`;
}

/**
 * Reads a scanned or pasted Rhema chat link, as https://host/chat/<digits> or rhema://chat/<digits>,
 * into its digits. Anything else gives null.
 */
export function chatLinkDigits(link: string): string | null {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch {
    return null;
  }
  let path: string;
  if (url.protocol === "rhema:") path = `${url.host}${url.pathname}`.replace(/^\/+/, "");
  else if (url.protocol === "https:" || url.protocol === "http:") path = url.pathname.replace(/^\/+/, "");
  else return null;
  const match = /^chat\/(\d{7,15})\/?$/.exec(path);
  return match?.[1] ?? null;
}

export type Country = { code: CountryCode; callingCode: string; name: string };

/** Every region with its calling code and its name in `locale`, sorted by name. */
export function countries(locale = "en"): Country[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale, "en"], { type: "region" });
  } catch {
    names = null;
  }
  return getCountries()
    .map((code) => ({
      code,
      callingCode: getCountryCallingCode(code),
      name: names?.of(code) ?? code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/** Splits a list into chunks of at most `size`, for calls with a per-request limit. */
export function chunk<T>(list: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}
